import { useState, useEffect, useRef } from 'react';
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js';
import { createDonation, ensureGuestSession, fetchStripeConfig, getClientId } from '../services/donationService';
import '../styles/Donation.css';

const purposes = [
  'Education', 'Medical', 'Child Care', 'Food Assistance', 'Housing', 'Disaster Relief',
  'Animal Welfare', 'Environment', 'Community Development', 'Women Empowerment',
  'Human Rights', 'Mental Health', 'Elderly Support', 'Sports & Recreation',
  'Technology Access', 'Clean Water', 'Religious Outreach', 'Public Health',
  'Education Materials', 'Refugee Support'
];

const Donation = () => {
  const [showNewDonation, setShowNewDonation] = useState(false);
  const [aboutNoteVisible, setAboutNoteVisible] = useState(true);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [recipientId, setRecipientId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [note, setNote] = useState('');
  const [selectedPurposes, setSelectedPurposes] = useState<Set<string>>(new Set());
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [savedPaymentMethodId, setSavedPaymentMethodId] = useState<string | null>(null);
  const [cardBrand, setCardBrand] = useState<string | null>(null);
  const [lastFour, setLastFour] = useState<string | null>(null);
  const [expiryMonth, setExpiryMonth] = useState<number | null>(null);
  const [expiryYear, setExpiryYear] = useState<number | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const cardElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const init = async () => {
      await getClientId();
      await ensureGuestSession();
      const publishableKey = await fetchStripeConfig();
      if (publishableKey) {
        const stripeInstance = await loadStripe(publishableKey);
        setStripe(stripeInstance);
      }
    };
    init();
  }, []);

  // Initialize Stripe Elements when modal opens
  useEffect(() => {
    if (showPaymentModal && stripe && !cardElement && cardElementRef.current) {
      const elements = stripe.elements();
      const card = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#1a1a2e',
            '::placeholder': { color: '#667085' }
          },
          invalid: { color: '#dc2626' }
        }
      });
      card.mount(cardElementRef.current);
      setCardElement(card);
      
      card.on('change', (event) => {
        const displayError = document.getElementById('card-errors');
        if (displayError) {
          displayError.textContent = event.error ? event.error.message : '';
        }
      });
    }
    
    return () => {
      if (cardElement) {
        cardElement.unmount();
        setCardElement(null);
      }
    };
  }, [showPaymentModal, stripe, cardElement]);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 2200);
  };

  const showManage = () => {
    setShowNewDonation(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showNewDonationForm = () => {
    resetForm();
    setShowNewDonation(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const togglePurpose = (purpose: string) => {
    const newSet = new Set(selectedPurposes);
    if (newSet.has(purpose)) {
      newSet.delete(purpose);
    } else {
      newSet.add(purpose);
    }
    setSelectedPurposes(newSet);
  };

  const resetForm = () => {
    setAmount('');
    setCurrency('USD');
    setRecipientId('');
    setStartDate('');
    setNote('');
    setSelectedPurposes(new Set());
    setIsAnonymous(false);
    setHasPaymentMethod(false);
    setSavedPaymentMethodId(null);
    setCardBrand(null);
    setLastFour(null);
    setExpiryMonth(null);
    setExpiryYear(null);
  };

  const openPaymentModal = () => {
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const handleSavePaymentMethod = async () => {
    if (!stripe || !cardElement) {
      showToast('Stripe not initialized. Please refresh.');
      return;
    }

    setIsLoading(true);
    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement
      });

      if (error) {
        showToast(error.message || 'Failed to process card');
        return;
      }

      setSavedPaymentMethodId(paymentMethod.id);
      setCardBrand(paymentMethod.card?.brand?.toUpperCase() || 'UNKNOWN');
      setLastFour(paymentMethod.card?.last4 || '0000');
      setExpiryMonth(paymentMethod.card?.exp_month || 0);
      setExpiryYear(paymentMethod.card?.exp_year || 0);
      setHasPaymentMethod(true);
      closePaymentModal();
      showToast('Payment method added successfully!');
    } catch (err) {
      showToast('Failed to process card. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDonate = async () => {
    const amountNum = Number(amount);

    if (!amountNum || amountNum <= 0) {
      showToast('Enter a valid donation amount first.');
      return;
    }

    if (!hasPaymentMethod || !savedPaymentMethodId) {
      showToast('Add a payment method before donating.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        amount: amountNum,
        currency: currency,
        doneeUserId: recipientId.trim() || null,
        startDateTime: startDate.trim() || null,
        note: note.trim() || null,
        anonymous: isAnonymous,
        purposeIds: Array.from(selectedPurposes),
        guestUserId: localStorage.getItem('guestUserId'),
        guestSessionId: localStorage.getItem('guestSessionId'),
        paymentMethod: {
          paymentMethodId: savedPaymentMethodId,
          paymentProcessor: 'STRIPE',
          methodType: 'CARD',
          cardBrand: cardBrand ?? 'UNKNOWN',
          lastFour: lastFour ?? '0000',
          expiryMonth: expiryMonth ?? 0,
          expiryYear: expiryYear ?? 0
        }
      };

      await createDonation(payload);
      showToast('Thank you! Your donation has been processed.');
      resetForm();
      showManage();
    } catch (err: any) {
      showToast(err.message || 'Donation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <section className="donation-page-hero">
        <h1>Give <span>Help</span>, Not Just Money</h1>
        <p>
          Your donation funds essential help requests for people facing real difficulties in their communities.
          Olitask does not take any cut for personal profit, every dollar is directed to verified support needs
          like groceries, medical transport, urgent home repairs, and critical errands.
        </p>
      </section>

      <main className="donation-page-wrap">
        <div className="donation-flow-head">
          <h2 className="donation-flow-title">{showNewDonation ? 'New Donation' : 'Manage Your Donations'}</h2>
          {showNewDonation && (
            <button className="donation-back-btn" onClick={showManage}>← Back</button>
          )}
        </div>

        {!showNewDonation ? (
          <div className="donation-manage-screen">
            <div className="donation-card">
              {aboutNoteVisible && (
                <article className="donation-about-note">
                  <button className="donation-note-close" onClick={() => setAboutNoteVisible(false)}>×</button>
                  <h3>About Donations</h3>
                  <p>
                    Donations on Olitask are used only to support people in need and are redirected to real help
                    requests, never for personal profit. Your generosity directly supports practical needs and helps
                    ensure no one has to face hardship alone.
                  </p>
                </article>
              )}
              <div className="donation-row">
                <button className="donation-btn-green" onClick={showNewDonationForm}>🎁 Donate Now 💪</button>
              </div>
              <div className="donation-empty">
                <p>No donations yet</p>
                <button className="donation-btn-green" onClick={showNewDonationForm}>Make a Donation</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="donation-new-screen">
            <div className="donation-grid-2">
              <article className="donation-panel">
                <h3>Donation Details</h3>
                <p className="donation-sub">Choose how much you would like to donate and optionally add a note and purpose.</p>

                <div className="donation-field">
                  <label>Amount</label>
                  <div className="donation-amount-row">
                    <input type="number" min="1" step="0.01" placeholder="e.g. 25.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    <span className="donation-currency-pill">USD</span>
                  </div>
                </div>

                <div className="donation-field">
                  <label>Currency</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    <option value="USD">USD</option>
                    <option value="UGX">UGX</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>

                <div className="donation-field">
                  <label>Recipient User ID (optional)</label>
                  <input type="text" placeholder="Enter recipient user ID" value={recipientId} onChange={(e) => setRecipientId(e.target.value)} />
                </div>

                <div className="donation-field">
                  <label>Start Date / Time (optional)</label>
                  <input type="text" placeholder="e.g. 2026-03-05T18:30:00Z" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>

                <div className="donation-field">
                  <label>Note (optional)</label>
                  <small>This note will be visible to the recipient so they understand the intention of your donation.</small>
                  <textarea rows={3} placeholder="Add a short note about the donation" value={note} onChange={(e) => setNote(e.target.value)} />
                </div>

                <div className="donation-field">
                  <label>Donate Anonymously</label>
                  <div className="donation-switch-row">
                    <small>If enabled, your name will not be shown to the recipient.</small>
                    <button className={`donation-switch ${isAnonymous ? 'on' : ''}`} onClick={() => setIsAnonymous(!isAnonymous)} />
                  </div>
                </div>
              </article>

              <article className="donation-panel">
                <h3>Purpose (Optional)</h3>
                <p className="donation-sub">Tag your donation with one or more purposes to help with tracking and reporting.</p>
                <div className="donation-chips">
                  {purposes.map((purpose) => (
                    <button
                      key={purpose}
                      className={`donation-chip ${selectedPurposes.has(purpose) ? 'active' : ''}`}
                      onClick={() => togglePurpose(purpose)}
                    >
                      {purpose}
                    </button>
                  ))}
                </div>

                <div className={`donation-payment-box ${hasPaymentMethod ? 'ready' : ''}`}>
                  <h4>{hasPaymentMethod ? 'Payment method added' : 'Add a payment method'}</h4>
                  <p>
                    {hasPaymentMethod 
                      ? `Card ending in ${lastFour} ready for donation.`
                      : 'To send this donation, you need at least one card or bank account set as your outgoing payment method.'}
                  </p>
                  <button className="donation-btn-blue" onClick={openPaymentModal}>
                    {hasPaymentMethod ? 'Change Card' : 'Add Payment Method'}
                  </button>
                </div>

                <div className="donation-actions">
                  <button className="donation-btn-red" onClick={showManage}>Cancel</button>
                  <button className="donation-btn-donate" onClick={handleDonate} disabled={isLoading}>
                    {isLoading ? 'Processing...' : 'Donate'}
                  </button>
                </div>
              </article>
            </div>
          </div>
        )}
      </main>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="donation-modal show">
          <div className="donation-modal-content">
            <div className="donation-modal-header">
              <h3>Add Payment Method</h3>
              <button className="donation-modal-close" onClick={closePaymentModal}>&times;</button>
            </div>
            <div className="donation-modal-body">
              <div ref={cardElementRef} className="donation-card-element"></div>
              <div id="card-errors" className="donation-card-errors"></div>
              <p className="donation-modal-info">Your card details are securely processed by Stripe.</p>
            </div>
            <div className="donation-modal-footer">
              <button className="donation-btn-red" onClick={closePaymentModal}>Cancel</button>
              <button className="donation-btn-green" onClick={handleSavePaymentMethod} disabled={isLoading}>
                {isLoading ? 'Processing...' : 'Save Card'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toastMessage && <div className="donation-toast show">{toastMessage}</div>}
    </>
  );
};

export default Donation;
